import { pick } from 'ramda'

/**
 * Google Ads click identifiers. Exactly one of these is present on a click,
 * depending on the surface: gclid for the web, gbraid and wbraid for app and
 * iOS traffic where a gclid cannot be set.
 */
const validClickIdNames = ['gclid', 'gbraid', 'wbraid'];

const ClickIdCookieName = 'ATHENASTUDIES-CLICK-ID';

// Google attributes an offline conversion to a click for at most 90 days, which
// is also the click-through conversion window on the conversion action. Keeping
// the identifier any longer only produces uploads Google will discard.
const ClickIdCookieSeconds = 90 * 24 * 60 * 60;

/**
 * Reads a Google Ads click identifier from the query string and stores it, so
 * the order it eventually leads to can be uploaded as an offline conversion.
 *
 * The most recent click wins, matching how Google attributes conversions. The
 * cookie is deliberately not consumed on use: one click can precede several
 * orders, and a visitor who abandons a basket should not lose the attribution
 * of the order they place a week later.
 *
 * @param {object} query The object of query parameters
 * @param {function} setCookie setCookie function from the react-cookie hook
 * @returns {boolean} True if a click identifier was present
 */
export function storeClickId(query, setCookie){
	const present = pick(validClickIdNames, query || {});
	const name = validClickIdNames.find((key) => {
		return typeof present[key] === 'string' && present[key].length > 0;
	});

	if(!name) return false;

	setCookie(ClickIdCookieName, `${name}:${present[name]}`, {
		maxAge: ClickIdCookieSeconds,
		path: '/',
	});

	return true;
}

/**
 * Get the ready to use query string with the stored Google Ads click
 * identifier, for the endpoints that record where a basket came from.
 *
 * @param {Cookies} cookies
 * @returns {string} The query string, or an empty string when there is nothing to send
 */
export function getClickIdQueryString(cookies){
	const stored = cookies.get(ClickIdCookieName);
	if(typeof stored !== 'string') return '';

	const separator = stored.indexOf(':');
	if(separator < 1) return '';

	const name = stored.slice(0, separator);
	const value = stored.slice(separator + 1);

	if(validClickIdNames.indexOf(name) === -1 || value.length === 0) return '';

	return `${name}=${encodeURIComponent(value)}`;
}
